import {defaultBoardLabels} from '@github-ui/board'
import type {BoardColumn} from '@github-ui/board-core'
import {useSuspenseQuery} from '@github-ui/react-query'
import {type ReactNode, Suspense, useMemo} from 'react'

import {BoardSurface} from './generic/BoardSurface'
import type {SavedViewsBoardAdapter, SavedViewsBoardItem, SavedViewsBoardOptions} from './types'

interface SavedViewBoardProps<TItem extends SavedViewsBoardItem, TColumnMetadata> {
  adapter: SavedViewsBoardAdapter<TItem, TColumnMetadata>
  options: SavedViewsBoardOptions
  renderCard: (item: TItem) => ReactNode
  renderColumnHeader?: (column: BoardColumn, metadata: TColumnMetadata) => ReactNode
}

export function SavedViewBoard<TItem extends SavedViewsBoardItem, TColumnMetadata = undefined>(
  props: SavedViewBoardProps<TItem, TColumnMetadata>,
) {
  return (
    <Suspense fallback={null}>
      <SavedViewBoardContent {...props} />
    </Suspense>
  )
}

function SavedViewBoardContent<TItem extends SavedViewsBoardItem, TColumnMetadata>({
  adapter,
  options,
  renderCard,
  renderColumnHeader,
}: SavedViewBoardProps<TItem, TColumnMetadata>) {
  const {data} = useSuspenseQuery({
    queryKey: ['saved-views-board', adapter.key, options],
    queryFn: ({signal}) => adapter.fetchInitialBoardPayload({...options, signal}),
  })
  const renderHeader = useMemo(
    () => (renderColumnHeader ? (column: BoardColumn) => renderColumnHeader(column, data.columnMetadata) : undefined),
    [renderColumnHeader, data],
  )

  return (
    <BoardSurface
      boardData={data.boardData}
      renderCard={renderCard}
      renderColumnHeader={renderHeader}
      labels={defaultBoardLabels}
    />
  )
}
